import { motion } from 'framer-motion';

import { RunStatus } from '../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import {
  PipelineProcessingStageDescriptions,
  PipelineProcessingStages,
  ProcessingStatus,
} from '@/new/lib/pipeline-status';

const getPipelineStatusIcon = (pipelineStatus: ProcessingStatus, index: number) => {
  const currentStage = PipelineProcessingStages[index];
  const isAwaitingApproval = currentStage === RunStatus.AwaitingApproval;

  switch (true) {
    case PipelineProcessingStages.indexOf(pipelineStatus) > index:
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
          }}
          className="flex items-center justify-center"
        >
          <Icon className="lucide--circle-check text-teal-500" />
        </motion.div>
      );
    case PipelineProcessingStages.indexOf(pipelineStatus) === index:
      if (isAwaitingApproval) {
        return <Icon className="lucide--shield-alert text-amber-600" />;
      }
      return <Icon className="lucide--loader-circle animate-spin text-blue-500 duration-1000" />;
  }
};

interface ProcessingPipelineProps {
  casePipelineStatus: RunStatus;
  requiresAdminApproval?: boolean;
}

export const ProcessingPipeline = ({ casePipelineStatus, requiresAdminApproval = false }: ProcessingPipelineProps) => {
  const stages = PipelineProcessingStages.filter(
    (status) =>
      status !== RunStatus.InQueue &&
      (status !== RunStatus.AwaitingApproval || casePipelineStatus === RunStatus.AwaitingApproval),
  );

  return (
    <div className="py-6">
      <div className="relative">
        {/* Continuous horizontal line through all stages */}
        <div className="absolute top-3 right-0 left-0 h-[2px] bg-gray-200" style={{ zIndex: 0 }} />

        {/* Stage icons and labels */}
        <div className="relative flex items-start justify-between" style={{ zIndex: 1 }}>
          {stages.map((status: ProcessingStatus) => {
            const currentIndex = PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus);
            const stageIndex = PipelineProcessingStages.indexOf(status);
            const isActive = stageIndex === currentIndex;
            const isCompleted = stageIndex < currentIndex;
            const isAwaitingApproval = status === RunStatus.AwaitingApproval;

            // Override the approval stage title if it requires admin approval
            const stageTitle =
              isAwaitingApproval && requiresAdminApproval
                ? 'Admin Approval'
                : PipelineProcessingStageDescriptions[status].title;

            return (
              <div key={status} className="flex flex-1 flex-col items-center">
                <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                  {getPipelineStatusIcon(casePipelineStatus as ProcessingStatus, stageIndex)}
                </div>
                <div
                  className={`mt-3 rounded px-3 py-1 text-center text-xs ${
                    isActive && isAwaitingApproval
                      ? 'bg-amber-50 text-amber-700'
                      : isActive
                        ? 'bg-blue-100 text-blue-700'
                        : isCompleted
                          ? 'bg-brandSecondary/20 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {stageTitle}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
